import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { AlertIconSVG, InfoIconSVG } from 's2s-svg-content';
import Action from 's2s-action';
import { colorGrid } from "s2s-themes";
import styled from 'styled-components';
// import LoggingService from 's2s-logging-service';
import { injectIntl } from 'react-intl';

const AlertItemContainer = styled.div`
  background-color: transparent;
  box-shadow: rgba(0, 0, 0, 0.5) 0px 2px 12px 0px;
  display: flex;
  flex-direction: column;
  margin-bottom: 10px;
  min-width: 320px;
  width: 100%;
  max-width: 360px;
`;

const AlertBody = styled.div`
  align-items: flex-start;
  background-color: #FFF;  
  display: flex;
  flex-direction: column;
  font-size: 14px;
  padding: 5px 10px 5px 10px;
  border-bottom-left-radius:4px;
  border-bottom-right-radius: 4px;
`;

const AlertBodyText = styled.span`
  flex: 1;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0.25px;
`;

const AlertLink = styled.a`
  flex: 1;
  margin-top: 10px;
`;

const AlertHeading = styled.div`
  border-top-left-radius:4px;
border-top-right-radius: 4px;
  align-items: 'center';
  background-color : ${props => {
    let bgColor;
    switch (props.alertLevel) {
      case 'error':
        bgColor = colorGrid.red8;
        break;
      case 'warn':
        bgColor = colorGrid.yellow6;
        break;
      case 'info':
        bgColor = colorGrid.green6;
        break;
      default:
        bgColor = colorGrid.red8;
    }
    return bgColor;
  }};
  color : ${colorGrid.gray0}; 
  display : flex;
  font-size: 14px;
  justify-content : center;
  font-weight: 600;
  font-size: 14px;
  line-height: 22px;
  letter-spacing: 0.1px;
  min-height : 48px;
`;

const AlertIconContainer = styled.span`
  align-items : center;
  display : flex;
  flex : 0;
  margin-right : 10px;
  margin-left : 8px;
`;

const AlertIconAndTextContainer = styled.span`
  display: flex;
  flex: 1;
`;

const AlertHeadingText = styled.span`
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const AlertDismissContainer = styled.span`
  align-items : flex-start;
  display : flex;
  flex : 0;
  margin-right: 8px;
  margin-top : 12px; // keep close icon at this position even when multiline heading text
`;


const AlertItem = (props) => {

  let alertTimeout = 0;

  useEffect(() => {
    // set timeout for removing the alert from list if autoCloseTimeout is > 0
    // and isPersistent is false
    if (!props.isPersistent && props.autoCloseTimeout > 0) {
      alertTimeout = setTimeout(() => {
        props.cbDismissAlert(props.alertId);
      }, props.autoCloseTimeout * 1000);
    }
    // cleanup function clears the timeout
    return () => {
      clearTimeout(alertTimeout);
    };
  }, []);


  const handleDismissClick = () => {
    clearTimeout(alertTimeout);
    // console.log('props.alertId', props);
    props.cbDismissAlert(props.alertId);
  };

  const handleAlertClick = () => {
    if (typeof (props.cbClick) === 'function') {
      // console.log('Clicking clicking clicking');
      props.cbClick();
    }
  };

  const getSVGName = () => {
    let svgName;
    switch (props.alertLevel) {
      case 'info':
        svgName = InfoIconSVG;
        break;
      case 'error':
      case 'warn':
      default:
        svgName = AlertIconSVG;
        break;
    }
    return svgName;
  };

  const renderSplitAlertBodyText = (splitString) => {
    return splitString.map((part, index) => {
      // console.log(part);
      return (
        <React.Fragment key={`${part}${index}`}>
          {part} <br />
        </React.Fragment>
      );
    });
  };

  //LoggingService.getInstance().debug('--------------> ');
  let alertDismissIcon;
  let alertBody;
  let alertLink;
  let DynamicSVG = getSVGName();

  if (!props.isPersistent) {
    alertDismissIcon = (<AlertDismissContainer className="dismissIconContainer" alertId={props.alertId}>
      <Action svgName="CloseIconSVG" svgStyle={{ fill: colorGrid.gray0, height: '24px', width: '24px' }} cbActionClick={handleDismissClick} />
    </AlertDismissContainer>);
  }

  if (props.alertBodyText) {
    if (props.alertLink) {
      alertLink = (<AlertLink className="detailLink" target="_blank" href={props.alertLink}
      >
        {props.alertLinkLabel ? props.alertLinkLabel : props.alertLink}
      </AlertLink>);
    }

    // LPAD-666 process alertBodyText so that the \n are converted to <br> for browser rendering

    alertBody = (
      <AlertBody className="alertBodyContainer">
        <AlertBodyText className="alertBodyText">
          {renderSplitAlertBodyText(props.alertBodyText.split("\n "))}
        </AlertBodyText>
        {alertLink}
      </AlertBody>
    );
  }

  return (
    <AlertItemContainer className="alertItemContainer" alertLevel={props.alertLevel}
      onClick={props.cbClick ? handleAlertClick : undefined}
    >
      <AlertHeading className="alertHeading" alertLevel={props.alertLevel}>
        <AlertIconAndTextContainer className="iconTextContainer">
          <AlertIconContainer className="iconContainer">
            <DynamicSVG className="alertSVG" svgStyle={{
              height: '24px',
              width: '24px',
              fill: colorGrid.gray0
            }} />
          </AlertIconContainer>
          <AlertHeadingText>{props.alertHeadingText}</AlertHeadingText>
        </AlertIconAndTextContainer>
        {alertDismissIcon}
      </AlertHeading>
      {alertBody}
    </AlertItemContainer>
  );
};

AlertItem.propTypes = {
  alertBodyText: PropTypes.string,
  alertHeadingText: PropTypes.string,
  alertId: PropTypes.number, // referred to when dismissing alerts via remove click
  alertLevel: PropTypes.oneOf(['error', 'warn', 'info']),
  alertLink: PropTypes.string, // if a link is to be in the alertBodyText
  alertLinkLabel: PropTypes.string, // label for
  autoCloseTimeout: PropTypes.number,
  cbDismissAlert: PropTypes.func,
  isPersistent: PropTypes.bool,
  type: PropTypes.string,  // 'noconnection', 'other' for dismissing error when back online
  cbClick: PropTypes.func
};

AlertItem.defaultProps = {
  alertBodyText: undefined,
  alertHeadingText: undefined,
  alertId: 0,
  autoCloseTimeout: 3,
  cbDismissAlert: () => { },
  isPersistent: false,
  type: 'other'
};


export default injectIntl(AlertItem);
